import { BASE_STORE_URL } from '@/lib/config';
import type { ImageLoaderProps } from 'next/image';

export const SIDEBAR_BANNER_FALLBACK_IMAGE = '/images/blog/sidebar-banner.webp';

export const blogImageUrl = (
  imageKey: string | null | undefined
): string | null => {
  if (!imageKey || imageKey === '/' || imageKey.trim() === '') {
    return null;
  }
  if (imageKey.startsWith('http://') || imageKey.startsWith('https://')) {
    return imageKey;
  }
  const key = imageKey.startsWith('/') ? imageKey.slice(1) : imageKey;
  return `${BASE_STORE_URL}/${key}`;
};

export const sidebarBannerImageUrl = (
  imageKey: string | null | undefined
): string => blogImageUrl(imageKey) || SIDEBAR_BANNER_FALLBACK_IMAGE;

const BLOG_IMAGE_WIDTHS = [
  2800, 2200, 1800, 1400, 1200, 1000, 800, 600, 400, 200,
] as const;

/** GCS keys from cloneImage: `{originalKey}-{width}`, not catalog `-600.webp`. */
export const blogImageLoader = ({ src, width }: ImageLoaderProps): string => {
  if (src.startsWith('/')) {
    return src;
  }
  const pickedWidth = BLOG_IMAGE_WIDTHS.find((step) => width >= step) ?? 2800;
  return `${src}-${pickedWidth}`;
};

export const estimateReadingMinutes = (
  html: string | null | undefined
): number => {
  if (!html) {
    return 1;
  }
  const text = html
    .replace(/<[^>]+>/g, ' ')
    .replace(/&[a-z]+;/gi, ' ')
    .replace(/\s+/g, ' ')
    .trim();
  const words = text ? text.split(' ').length : 0;
  return Math.max(1, Math.round(words / 200));
};

export const blogReadingMinutes = (post: {
  readingMinutes?: number | null;
  content?: string | null;
  introText?: string | null;
}): number => {
  const minutes = post.readingMinutes;
  if (typeof minutes === 'number' && Number.isFinite(minutes) && minutes > 0) {
    return minutes;
  }
  return estimateReadingMinutes(post.content || post.introText);
};

/** Old archive rows: `06/06/2026`. Parsed from ISO prefix to avoid TZ shifts. */
export const formatBlogArchiveDate = (
  iso: string | null | undefined
): string => {
  const parts = parseBlogCalendarDate(iso);
  if (!parts) {
    return '';
  }
  const day = String(parts.day).padStart(2, '0');
  const month = String(parts.month).padStart(2, '0');
  return `${day}/${month}/${parts.year}`;
};

const parseBlogCalendarDate = (
  iso: string | null | undefined
): { year: number; month: number; day: number } | null => {
  if (!iso) {
    return null;
  }
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso);
  if (!match) {
    return null;
  }
  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);
  if (month < 1 || month > 12 || day < 1 || day > 31) {
    return null;
  }
  return { year, month, day };
};

export const formatBlogDate = (
  iso: string | null | undefined,
  locale: string
): string => {
  const parts = parseBlogCalendarDate(iso);
  if (!parts) {
    return '';
  }
  return new Intl.DateTimeFormat(locale === 'en' ? 'en-GB' : 'ru-RU', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    timeZone: 'UTC',
  }).format(new Date(Date.UTC(parts.year, parts.month - 1, parts.day)));
};

export const formatBlogRelativeDate = (
  iso: string | null | undefined,
  locale: string
): string => {
  if (!iso) {
    return '';
  }
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) {
    return '';
  }

  const diffMs = date.getTime() - Date.now();
  const absMs = Math.abs(diffMs);
  const minute = 60 * 1000;
  const hour = 60 * minute;
  const day = 24 * hour;
  const month = 30 * day;
  const year = 365 * day;
  const rtf = new Intl.RelativeTimeFormat(locale === 'en' ? 'en' : 'ru', {
    numeric: 'always',
  });

  if (absMs < hour) {
    const minutes = Math.round(diffMs / minute);
    return rtf.format(
      minutes === 0 ? (diffMs < 0 ? -1 : 1) : minutes,
      'minute'
    );
  }

  if (absMs < day) {
    return rtf.format(Math.round(diffMs / hour), 'hour');
  }

  if (absMs < month) {
    return rtf.format(Math.round(diffMs / day), 'day');
  }

  if (absMs < year) {
    return rtf.format(Math.round(diffMs / month), 'month');
  }

  return rtf.format(Math.round(diffMs / year), 'year');
};

export const blogPostHref = (post: {
  categorySlug: string;
  id: number;
}): string => `/blog/${post.categorySlug}/${post.id}`;
